import React, {
  useContext,
  createContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useFight } from './Fight';
import { FightAction } from '../models/fight';
import { useSocketV2Instance } from './index';
import { belongsToFight, belongsToFightStrict } from '../utils/legacyId';
import { actionsReducer, ActionReducer } from './actionsReducer';
import { domainKeyFor } from '../utils/fightKeys';
import apiV2 from '../services/api-mike-v2';
import { useAuth } from './Auth';

export interface ActionsContextData {
  actions: Array<FightAction>;
  dispatchAction(action: ActionReducer): void;
  fetchActions(): void;
}



const ActionsContext = createContext<ActionsContextData>(
  {} as ActionsContextData,
);

const ActionsProvider: React.FC = ({ children }) => {
  const { dispatchFight, fight, fetchFight, getFight, updateFight } = useFight();

  const { token } = useAuth();
  const { on, off, isConnected, instanceId } = useSocketV2Instance();

  const memoReducer = useCallback(
    (state: FightAction[], action: ActionReducer) =>
      actionsReducer(fight, state, action),
    [fight],
  );

  const [actions, dispatchAction] = useReducer(memoReducer, []);

  const reconcileDomain = useCallback(
    (action: FightAction | undefined) => {
      if (!action) return;

      const current = getFight();

      // O socket entrega actions de outras lutas; mesmo guard dos reducers.
      if (!belongsToFight(action, current)) return;

      if (action.name === 'controlStarted') {
        updateFight({
          currentDomain: domainKeyFor(
            current.fightType,
            action.corner,
            action.stance,
          ),
          currentStance: action.stance,
        });
        return;
      }

      if (action.name === 'controlEnded') {
        // O scout recebe o eco das próprias actions, e uma troca de domínio
        // emite `controlEnded(antigo)` logo antes de `controlStarted(novo)`.
        // Sem este guard o eco do primeiro apagaria o domínio recém-aberto.
        const endedDomain = domainKeyFor(
          current.fightType,
          action.corner,
          action.stance,
        );

        if (endedDomain && endedDomain !== current.currentDomain) return;

        updateFight({ currentDomain: null, currentStance: 'standing' });
      }
    },
    [getFight, updateFight],
  );

  const fetchActions = useCallback(async () => {
    try {
      // Busca pelo UUID: o backend detecta o formato e filtra por fightNewId,
      // então funciona também para lutas sem id legado — que antes nem
      // chegavam a buscar, por causa do `legacyId !== 0`.
      if (!fight.id) return;

      const response = await apiV2.get(`/actions?fightId=${fight.id}`, {
        headers: { pageSize: 200 },
      });
      const fetched: FightAction[] = response.data.data;
      dispatchAction({ type: 'fetch', init: fetched });

      const lastControl = fetched.find(
        (action) =>
          action.name === 'controlStarted' || action.name === 'controlEnded',
      );

      if (lastControl?.name === 'controlStarted') reconcileDomain(lastControl);
      else updateFight({ currentDomain: null, currentStance: 'standing' });
    } catch (error) {
      console.error((error as Error).message);
    }
  }, [fight.id, dispatchAction, reconcileDomain, updateFight]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  useEffect(() => {
    if (!isConnected) return;

    const handleRecorded = (payload: FightAction) => {
      // A sala é do evento inteiro: filtra antes de qualquer efeito colateral.
      // `getFight()` é o espelho síncrono — o `fight` do closure fica velho
      // entre o dispatch e o render seguinte.
      if (!belongsToFight(payload, getFight())) return;

      const params = { type: 'add', payload };
      dispatchAction(params);
      dispatchFight(params);
      reconcileDomain(payload);
    };

    on('recorded', handleRecorded);

    return () => {
      off('recorded', handleRecorded);
    };
  }, [dispatchFight, isConnected, on, off, dispatchAction, reconcileDomain, getFight, token, instanceId]);

  useEffect(() => {
    if (!isConnected) return;

    const handleReset = (payload: FightAction) => {
      // Guard estrito: o reset de outra luta do card zeraria o scout inteiro,
      // e o `fetchFight()` abaixo dispararia um refetch em todo mundo na sala.
      if (!belongsToFightStrict(payload, getFight())) return;

      const params = { type: 'reset', payload };
      dispatchAction(params);
      dispatchFight(params);

      fetchFight();
    };

    on('reset', handleReset);

    return () => {
      off('reset', handleReset);
    };
  }, [dispatchFight, fetchFight, isConnected, on, off, dispatchAction, getFight, token, instanceId]);

  useEffect(() => {
    if (!isConnected) return;

    const handleUpdated = (payload: FightAction) => {
      if (!belongsToFight(payload, getFight())) return;

      // Só a lista. O reducer de fight não sabe calcular o delta de uma edição,
      // e o caso real — editar o resultado da luta — não mexe em contagem de
      // golpes, então recalcular o outcome aqui só arriscaria corrompê-lo.
      dispatchAction({ type: 'update', payload });
    };

    on('updated', handleUpdated);

    return () => {
      off('updated', handleUpdated);
    };
  }, [isConnected, on, off, dispatchAction, getFight, token, instanceId]);

  useEffect(() => {
    if (!isConnected) return;

    const handleRevoked = (payload: FightAction) => {
      // Também destrutivo: revogar decrementa o outcome da luta aberta.
      if (!belongsToFightStrict(payload, getFight())) return;

      const params = { type: 'remove', payload };
      dispatchAction(params);
      dispatchFight(params);
    };

    on('revoked', handleRevoked);

    return () => {
      off('revoked', handleRevoked);
    };
  }, [dispatchFight, isConnected, on, off, dispatchAction, getFight, token, instanceId]);

  const contextValue = useMemo(
    () => ({
      actions,
      dispatchAction,
      fetchActions,
    }),
    [actions, dispatchAction, fetchActions]
  );

  return (
    <ActionsContext.Provider value={contextValue}>
      {children}
    </ActionsContext.Provider>
  );
};

function useActions(): ActionsContextData {
  const context = useContext(ActionsContext);

  if (!context) {
    throw new Error('useActions must be used within ActionsProvider');
  }

  return context;
}

export { useActions, ActionsProvider };
